import { useEffect } from "react";

import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { ErrorResponse, SuccessfulResponse } from "@/api/fetcher_v2";
import {
  getSearchMentionsV4,
  getTagMentionsV4,
} from "@/api/endpoints/searches";
import Container from "@/components/Container";
import PaginationNew from "@/components/PaginationNew";
import Episodes from "@/containers/Episodes";
import { MentionCardsProvider } from "@/containers/MentionCardsContext";
import TagContext from "@/contexts/TagContext";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { usePageParam } from "@/hooks/usePageParam";
import { usePublicReport } from "@/hooks/usePublicReport";
import useSearchParams from "@/hooks/useSearchParams";
import { PaginatedMentionCards } from "@/types/PaginatedMentionCards";
import SharedLinkLayout from "@components/SharedLinkLayout";
import Typography from "@components/Typography";

import styles from "./PublicAlertView.module.css";
import MentionCardNew, {
  SkeletonMentionCard,
} from "@components/MentionCardNew";

const PublicAlertViewInner = ({
  title,
  mentionCardsData,
  isMentionCardsLoading,
}: {
  title: string;
  mentionCardsData:
    | ErrorResponse<unknown>
    | SuccessfulResponse<PaginatedMentionCards>
    | undefined;
  isMentionCardsLoading: boolean;
}) => {
  const [searchParams] = useSearchParams<{
    sentiment: boolean;
    kpis: boolean;
    page: number;
  }>();
  const [page, setPage] = usePageParam();

  const hasSentiment = searchParams.sentiment;
  const hasKpi = searchParams.kpis;

  const mentionCards = mentionCardsData?.ok
    ? mentionCardsData.data.results
    : [];
  const count = mentionCardsData?.ok ? mentionCardsData.data.count : 0;
  const pageCount = mentionCardsData?.ok
    ? mentionCardsData.data.page_count
    : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams.page]);

  return (
    <SharedLinkLayout>
      <header className={styles.header}>
        <Typography className={styles.title} variant="h1">
          {title}
        </Typography>
        {count ? (
          <Typography
            data-testid="public-report-subtitle"
            className={styles.subtitle}
            variant="subtitle-1"
          >
            {L.PUBLIC_ALERT_SUBTITLE.replaceAll("{}", count)}
          </Typography>
        ) : isMentionCardsLoading ? null : (
          <Typography className={styles.subtitle} variant="subtitle-1">
            {L.PUBLIC_ALERT_EMPTY_SUBTITLE}
          </Typography>
        )}
      </header>

      <Container>
        <Episodes>
          {isMentionCardsLoading ? (
            <>
              <SkeletonMentionCard />
              <SkeletonMentionCard />
              <SkeletonMentionCard />
            </>
          ) : (
            <MentionCardsProvider>
              {mentionCards.map((mentionCard) => (
                <MentionCardNew
                  key={mentionCard.episode.guid}
                  hideSentiment={!hasSentiment}
                  mentionCard={mentionCard}
                  hidePrValue={!hasKpi}
                  hideReach={!hasKpi}
                  readOnly
                />
              ))}
            </MentionCardsProvider>
          )}
        </Episodes>

        {pageCount && pageCount > 1 ? (
          <PaginationNew
            className="mt-8"
            page={page}
            max={pageCount}
            onChangePage={setPage}
          />
        ) : null}
      </Container>
    </SharedLinkLayout>
  );
};

const PublicTagContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <TagContext.Provider
    value={{
      organizationTags: [],
      setOrganizationTags: () => undefined,
      fetchOrganizationTags: () => undefined,
    }}
  >
    {children}
  </TagContext.Provider>
);

export const PublicAlertView = () => {
  // URL state
  const { uuid } = useParams<{ uuid: Uuid }>();
  const [filters] = useFilterParamsV4();
  const [page] = usePageParam();

  // Server state
  const { data: reportData } = usePublicReport(uuid);
  const { title = "" } = reportData?.ok ? reportData.data : {};

  const { data: mentionCardsData, isLoading: isMentionCardsLoading } = useQuery(
    {
      queryKey: ["public-searches", uuid, "mention-cards", filters, page],
      queryFn: () => getSearchMentionsV4(uuid, { ...filters, page }),
    }
  );

  return (
    <PublicTagContextProvider>
      <PublicAlertViewInner
        title={title}
        mentionCardsData={mentionCardsData}
        isMentionCardsLoading={isMentionCardsLoading}
      />
    </PublicTagContextProvider>
  );
};

export const PublicTagView = () => {
  // URL state
  const { id: token } = useParams<{ id: string }>();
  const [data] = atob(token).split(":");
  const jsonString = atob(data);
  const { org_uuid: orgUuid, tag } = JSON.parse(jsonString);
  const [filters] = useFilterParamsV4();
  const [page] = usePageParam();

  // Server state
  const { data: mentionCardsData, isLoading: isMentionCardsLoading } = useQuery(
    {
      queryKey: ["public-tags", orgUuid, tag, "mention-cards", filters, page],
      queryFn: () => getTagMentionsV4(tag, { ...filters, page }, orgUuid),
    }
  );

  return (
    <PublicTagContextProvider>
      <PublicAlertViewInner
        title={tag}
        mentionCardsData={mentionCardsData}
        isMentionCardsLoading={isMentionCardsLoading}
      />
    </PublicTagContextProvider>
  );
};
